<template>
  <div>
    <template v-if="canManageTags">
      <div class="tags-header">
        <div class="tag-input">
          <v-text-field
            v-model="currentTag.name"
            ref="input"
            :placeholder="$t('message.reviews.tags.typeNewTag')"
            clearable
            hide-details
            @keyup.enter="currentTag.name ? save() : void 0"
            @click:clear="setCurrentTag"
          ></v-text-field>
          <ui-button
            color="primary"
            class="ml-3"
            min-width="138px"
            :disabled="!currentTag.name"
            @click="save"
          >
            {{ $t(`message.common.${currentTag.id ? "save" : "add"}`) }}
          </ui-button>
        </div>
      </div>
      <div class="tags-body-header">
        {{ $t("message.reviews.tags.addedTags") }}:
      </div>
      <div class="tags-body">
        <TagsManagerList
          v-if="localTags.length"
          :tags="localTags"
          edit-enabled
          remove-enabled
          :auto-categorization-settings="autoCategorizationSettings"
          :show-auto-categorization-icon="showAutoCategorizationIcon"
          :show-sentiment-selector="showSentimentSelector"
          @edit="onEdit"
          @remove="showDeletePopup"
          @change-auto="changeAuto"
        />
        <div v-else class="tags-empty">
          {{ $t("message.reviews.tags.noTagsDescription") }}
          {{ $t("message.reviews.tags.addDescription") }}
        </div>
      </div>
    </template>
    <div v-else>
      <p class="no-permission">
        {{ $t("message.reviews.tags.noPermissions") }}
      </p>
    </div>
    <ui-dialog :show="showAlert">
      <p
        class="text--primary"
        v-html="$t('message.reviews.tags.tagDeleteAlert', [selectedTagName])"
      ></p>
      <div class="d-flex justify-center py-4">
        <ui-button class="mr-3" @click="hideDeletePopup">
          {{ $t("message.common.no") }}
        </ui-button>
        <ui-button color="primary" @click="onRemove" :loading="loading">
          {{ $t("message.common.confirmDelete") }}
        </ui-button>
      </div>
    </ui-dialog>
    <SectionLoader v-if="isLoading" :status="isLoading" :opacity="0.8" />
  </div>
</template>

<script>
import serviceHandler from "@/helpers/handlers/service";
import tagsService from "@/services/tags";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import TagsManagerList from "@/components/Tags/TagsManagerList";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import AnalyticsService from "@/services/analytics";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { endpoints } from "@/config/endpoints";

const analyticsService = new AnalyticsService(endpoints.analytics.gmb);

export default {
  name: "TagsManagerListContent",
  components: { TagsManagerList, SectionLoader },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    showSentimentSelector: {
      type: Boolean,
      default: true
    },
    tags: {
      type: Array,
      default: () => []
    },
    canManageTags: {
      type: Boolean,
      default: false
    },

    isLoading: {
      type: Boolean,
      required: true
    },
    showAutoCategorizationIcon: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      settings: null,
      showCategories: true,
      currentTag: null,
      localTags: [],
      loading: false,
      showAlert: false,
      selectedTag: null,
      phrasesGroup: null,
      allPhrases: null,
      autoCategorizationSettings: {
        isVisible: false,
        isDisabled: false
      }
    };
  },
  computed: {
    selectedTagName() {
      return this.selectedTag ? this.selectedTag.name : "";
    }
  },
  async created() {
    if (this.canManageTags) {
      this.loading = true;
      this.setCurrentTag();
      this.localTags = [...this.tags];
      this.loading = false;
      await this.getAutoCategorizationSettings();
    }
  },
  methods: {
    isFeatureFlagEnabled,
    async fetchTags() {
      this.localTags = await serviceHandler(tagsService.getTags());
    },
    async save() {
      const { id, name } = this.currentTag;
      this.loading = true;
      if (id) {
        await serviceHandler(tagsService.editTag(id, { name }));
      } else {
        await serviceHandler(tagsService.addTag(name));
      }
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_MANAGE,
        name: name,
        action: id ? "edit" : "add"
      });
      this.setCurrentTag();
      await this.fetchTags();
      this.onListUpdate();
      this.loading = false;
    },
    async changeAuto({ is_auto_categorized, id, name }) {
      await serviceHandler(tagsService.editTag(id, { is_auto_categorized }));
      await this.getAutoCategorizationSettings();

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORY_AUTO_STATUS_CHANGE,
        category_name: name,
        category_id: id,
        component: "categories_mgmt",
        new_status: is_auto_categorized ? "on" : "off"
      });
    },
    async getAutoCategorizationSettings() {
      try {
        const {
          max_ai_categories,
          auto_categorized_categories_count
        } = await analyticsService.getCategorizationCompanySettings();

        this.setAutoCategorizationSettings(
          true,
          auto_categorized_categories_count >= max_ai_categories
        );
      } catch (e) {
        this.setAutoCategorizationSettings(false, true);
      }
    },
    setAutoCategorizationSettings(isVisible, isDisabled) {
      this.autoCategorizationSettings.isVisible = isVisible;
      this.autoCategorizationSettings.isDisabled = isDisabled;
    },
    setCurrentTag(tag) {
      if (tag) {
        this.currentTag = { ...tag };
      } else {
        this.currentTag = { id: null, name: null, is_auto_categorized: null };
      }
      this.$emit("select-current-tag", this.currentTag);
    },

    onEdit(tag) {
      this.setCurrentTag(tag);
      this.$refs.input.focus();
    },

    showDeletePopup(tag) {
      this.selectedTag = tag;
      this.showAlert = true;
    },

    hideDeletePopup() {
      this.selectedTag = null;
      this.showAlert = false;
    },

    async onRemove() {
      this.loading = true;
      await serviceHandler(tagsService.removeTag(this.selectedTag.id));
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_MANAGE,
        name: this.selectedTag.name,
        action: "delete"
      });
      if (this.currentTag.id === this.selectedTag.id) {
        this.setCurrentTag();
      }
      this.onListUpdate();
      this.loading = false;
      this.hideDeletePopup();
    },

    async onListUpdate() {
      await this.fetchTags();
      this.$emit("list-update", [...this.localTags]);
    },

    close() {
      this.$emit("close");
    },
    async showPhrases(tag) {
      this.setCurrentTag(tag);
      this.$emit("show-phrases", tag);
    }
  },
  watch: {
    isOpen(value) {
      if (value) {
        this.localTags = [...this.tags];
      }
    },
    tags(current) {
      this.localTags = [...current];
    }
  }
};
</script>

<style lang="scss" scoped>
.tag-input {
  display: flex;
}

.tags-header {
  padding: $spacing * 2.5 0;
  border-top: 1px solid $light-3;
  border-bottom: 1px solid $light-3;

  &--no-border {
    border: none;
  }
}

.tags-body-header {
  color: $dark-blue-3;
  @include font-size(14px);
  line-height: 17px;
  font-weight: 600;
  padding: $spacing * 2 0;
}

.tags-body {
  flex: 1;
  height: 300px;
  overflow-x: hidden;
  overflow-y: auto;
}

.tags-empty {
  @include font-size(14px);
  line-height: 17px;
  font-weight: 500;
  color: rgba($dark-blue-3, 0.4);
}

.no-permission {
  text-align: center;
  white-space: pre-line;
  margin-bottom: $spacing * 8;
}

.dialog-title {
  min-height: 28px;
}
</style>
